import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import "./ReportAnalysisPage.css";
import { Context } from "../../context/Context";
import { Stepper } from "../ui";
import { fetchStoresAPI } from "../../utils/api/stores";
import { commitAnalysisAPI, downloadReportPartAPI, getAnalysisStatusAPI, getStoreReportPartsAPI, previewAnalysisAPI, validateReportFilesAPI } from "../../utils/api/reportAnalysis";
import { openLudFile } from "../ludDocuments/utils/ludDocuments.helpers";
import AnalysisSetup from "./AnalysisSetup";
import FilesStep from "./FilesStep";
import ChecksStep from "./ChecksStep";
import PreviewStep from "./PreviewStep";
import ResultStep from "./ResultStep";
import { buildReview } from "./reviewModel";
import { STEPS, initialState, reducer, setupComplete, submission } from "./reportAnalysisModel";
import { TEXT, errorText } from "./reportAnalysisText";
import { periodLabel } from "./format";

const codeOf = (error) => error?.data?.code || (error?.status === 0 ? "NETWORK_ERROR" : null);
const CONFIRMATION_CODES = ["CONFIRMATION_REQUIRED", "CONFIRMATION_INVALID", "VALIDATION_FAILED", "ACKNOWLEDGEMENT_REQUIRED"];
const SLOT_CODES = ["SLOT_INCOMPLETE", "VERSION_CONFLICT", "FILE_SLOT_CONFLICT"];

// /auth/lud - report analysis from the original files in five steps on the page:
//   1 Store & Period  2 Applications & Files  3 Checks  4 Preview  5 Save & Result
// Backend: POST report/validate-files (step 3), report/analyze-files/preview (step 4) and
// report/analyze-files (step 5). The browser never reads the spreadsheets: it only holds and
// uploads them. Moving between steps never saves; only "Confirm & Save" does.
export default function ReportAnalysisPage() {
  const { lang = "ar", handleTokenExpiration } = useContext(Context);
  const text = TEXT[lang] || TEXT.ar;
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("auth-token") : null;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [limits, setLimits] = useState({});
  const [stores, setStores] = useState([]);
  const stateRef = useRef(state);
  stateRef.current = state;
  const busyRef = useRef(false);
  const phaseRef = useRef("idle"); // the request stage a failure happened in
  // The running request: leaving its step aborts it and bumps `id`, so its late answer is ignored.
  const runRef = useRef({ id: 0, controller: null });
  const blobs = useRef(new Map()); // stored bytes of kept parts, by part + version (memory only)
  const stepRef = useRef(null);
  const firstStep = useRef(true);

  const { context } = state;
  const busy = state.phase !== "idle";
  const committing = state.phase === "committing" || phaseRef.current === "committing";
  const plan = useMemo(() => submission(state), [state]);
  const tooManyFiles = limits.maxFiles && plan.files.length > limits.maxFiles;

  useEffect(() => {
    if (!token) {
      handleTokenExpiration?.();
      return;
    }
    getAnalysisStatusAPI(token).then((value) => setLimits(value.limits || {})).catch(() => setLimits({}));
    fetchStoresAPI(token, []).then((list) => setStores((list || []).filter((store) => store.status === "active"))).catch(() => setStores([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadParts = useCallback(async (storeCode) => {
    if (!storeCode || !token) return;
    try {
      const parts = await getStoreReportPartsAPI(token, storeCode);
      dispatch({ type: "parts", parts });
    } catch (error) {
      dispatch({ type: "error", error: { code: codeOf(error) || "NETWORK_ERROR", status: error?.status, step: "files", notice: true } });
    }
  }, [token]);

  useEffect(() => {
    blobs.current.clear();
    if (context.storeCode) loadParts(context.storeCode);
  }, [context.storeCode, loadParts]);

  // Unsaved files: warn before leaving the page; the files themselves stay in memory only.
  useEffect(() => {
    const pending = state.uploads.length > 0 && !state.result;
    if (!pending) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = text.leaveWarning;
      return text.leaveWarning;
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [state.uploads.length, state.result, text.leaveWarning]);

  // A new step moves keyboard and screen-reader focus to its heading.
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    const heading = stepRef.current?.querySelector("h3");
    if (heading) {
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: false });
    }
  }, [state.step]);

  // The files exactly as they will be sent: new uploads, replacements, and the stored bytes of kept parts.
  const buildFiles = async () => {
    const files = [];
    for (const item of plan.files) {
      if (!item.keep) {
        files.push({ ...item, file: item.file, name: item.name });
        continue;
      }
      const key = `${item.partKey}:${item.expectedVersion}`;
      if (!blobs.current.has(key)) blobs.current.set(key, await downloadReportPartAPI(token, context.storeCode, item.part));
      const { blob, name } = blobs.current.get(key);
      files.push({ ...item, file: blob, name });
    }
    return files;
  };

  const ingestionContext = { storeId: context.storeCode, mode: context.mode, year: context.year, month: context.month };

  // A refused request: where its answer belongs (the step that can show or correct it).
  const handleError = async (error, phase, step) => {
    if (error?.status === 401) {
      handleTokenExpiration?.();
      return;
    }
    const code = codeOf(error);
    const data = error?.data || {};
    const failure = { code, status: error?.status, phase, step, details: data };
    if (CONFIRMATION_CODES.includes(code) && data.validation) {
      dispatch({ type: "validated", validation: data.validation });
      dispatch({ type: "error", error: { ...failure, step: "validate", notice: true } });
      return;
    }
    if (SLOT_CODES.includes(code)) {
      blobs.current.clear();
      await loadParts(context.storeCode);
      dispatch({ type: "error", error: { ...failure, step: "files", notice: true }, reset: { validation: null, preview: null, confirmationToken: null, acknowledgements: [] } });
      return;
    }
    if (code === "PREVIEW_REQUIRED" && data.planFingerprint) {
      const { planFingerprint, rows, files, stale } = data;
      dispatch({ type: "previewed", preview: { ...(stateRef.current.preview || {}), planFingerprint, rows, files, stale, conflicts: [], conflictCount: 0 } });
      dispatch({ type: "error", error: { ...failure, step: "preview", notice: true } });
      return;
    }
    if (["PROTECTED_ROW_CONFLICT", "RECONCILIATION_UNSAFE"].includes(code) && data.conflicts) {
      dispatch({ type: "previewed", preview: { ...(stateRef.current.preview || {}), conflicts: data.conflicts || [], conflictCount: data.conflictCount || 1 } });
      dispatch({ type: "error", error: { ...failure, step: "preview", notice: true } });
      return;
    }
    dispatch({ type: "error", error: failure });
  };

  // One request at a time. `step` dispatches only while this run is still current: after the user
  // left a check or preview (cancelRun) nothing it returns reaches the page.
  const run = async (phase, step, work) => {
    if (busyRef.current) return;
    busyRef.current = true;
    const id = runRef.current.id + 1;
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    runRef.current = { id, controller };
    const live = () => runRef.current.id === id;
    const emit = (action) => live() && dispatch(action);
    const ctx = { live, step: emit, signal: controller?.signal, onProgress: (value) => emit({ type: "progress", progress: value }) };
    // A save is "committing" from its first moment (also while its files are prepared), so it can
    // never be cancelled before the server's answer.
    const first = phase === "committing" ? "committing" : "preparing";
    try {
      phaseRef.current = first;
      emit({ type: "busy", phase: first, step });
      const files = await buildFiles();
      if (!live()) return;
      phaseRef.current = phase;
      emit({ type: "busy", phase, progress: 0 });
      await work(files, ctx);
    } catch (error) {
      if (live()) await handleError(error, phaseRef.current, step);
    } finally {
      if (live()) {
        busyRef.current = false;
        phaseRef.current = "idle";
      }
    }
  };
  // Leaving a running check or preview: abort it and ignore whatever it returns.
  const cancelRun = () => {
    const { controller } = runRef.current;
    runRef.current = { id: runRef.current.id + 1, controller: null };
    controller?.abort();
    busyRef.current = false;
    phaseRef.current = "idle";
  };

  const navigate = (step) => {
    if (committing) return;
    if (busyRef.current) {
      cancelRun();
      dispatch({ type: "cancelled", step });
      return;
    }
    dispatch({ type: "goto", step });
  };

  // Step 2 -> 3: problems found on the files are shown in Checks without sending anything.
  const checkFiles = () => {
    if (plan.blockers.some((blocker) => !blocker.warningOnly) || tooManyFiles) {
      dispatch({ type: "precheck" });
      return;
    }
    run("validating", "validate", async (files, ctx) => {
      const validation = await validateReportFilesAPI(token, ingestionContext, files, { onProgress: ctx.onProgress, signal: ctx.signal });
      ctx.step({ type: "validated", validation });
    });
  };
  // Step 3 -> 4 (never a save). With warnings: the server's token and the acknowledged codes.
  const previewWith = (confirmationToken, acknowledgements) => run("previewing", "preview", async (files, ctx) => {
    const preview = await previewAnalysisAPI(token, ingestionContext, files, { confirmationToken, acknowledgements, onProgress: ctx.onProgress, signal: ctx.signal });
    ctx.step({ type: "previewed", preview });
  });
  const continueToPreview = () => previewWith(state.confirmationToken, state.acknowledgements);
  const acknowledge = (confirmationToken, acknowledgements) => {
    dispatch({ type: "confirmed", token: confirmationToken, acknowledgements });
    previewWith(confirmationToken, acknowledgements);
  };
  // Step 4 -> 5: the only save. Requires the preview's plan fingerprint (PreviewStep: review.canSave).
  const commit = () => run("committing", "result", async (files, ctx) => {
    const current = stateRef.current;
    const result = await commitAnalysisAPI(token, ingestionContext, files, { confirmationToken: current.confirmationToken, acknowledgements: current.acknowledgements, planFingerprint: current.preview?.planFingerprint, onProgress: ctx.onProgress });
    ctx.step({ type: "committed", result });
    blobs.current.clear();
    loadParts(context.storeCode);
  });
  const startNew = () => {
    blobs.current.clear();
    dispatch({ type: "restart" });
    loadParts(context.storeCode);
  };

  const storeName = stores.find((item) => item.id === context.storeCode)?.businessName || context.storeCode;
  const review = buildReview(state, {
    storeName,
    isKnownWarning: (code) => Boolean(text.issues[code]),
    blockers: state.result || !state.precheck ? [] : plan.blockers,
    planned: state.precheck ? plan.files : [],
    maxFiles: state.precheck ? limits.maxFiles || null : null,
  });
  const editable = !busy && !state.result;

  const reachable = {
    setup: !state.result,
    files: !state.result && setupComplete(state),
    validate: !state.result && Boolean(state.validation || state.precheck || state.error?.step === "validate"),
    preview: !state.result && Boolean(state.preview || state.error?.step === "preview"),
    result: Boolean(state.result || state.error?.step === "result"),
  };
  const done = {
    setup: setupComplete(state),
    files: Boolean(state.validation || state.precheck || state.result),
    validate: Boolean(state.preview || state.result),
    preview: Boolean(state.result),
    result: Boolean(state.result),
  };
  const filesNotice = state.error?.step === "files" ? errorText(text, state.error) : null;

  return (
    <div className="report-analysis-page" dir={lang === "ar" ? "rtl" : "ltr"}>
      <header className="report-analysis-header">
        <h2>{text.title}</h2>
        <p className="report-analysis-muted">{text.subtitle}</p>
        <Stepper
          className="report-analysis-stepper"
          label={text.flow.stepper}
          steps={STEPS.map((key) => ({ key, label: text.flow.stepLabels[key] }))}
          current={state.step}
          isDone={(key) => done[key]}
          isReachable={(key) => reachable[key]}
          onSelect={committing ? undefined : navigate}
        />
        {state.step !== "setup" && setupComplete(state) && (
          <p className="report-analysis-context" data-testid="analysis-context">
            <strong>{storeName}</strong> · {periodLabel({ year: Number(context.year), month: Number(context.month) }, lang)} · {text.modes[context.mode]}
          </p>
        )}
      </header>

      <div ref={stepRef} className="report-analysis-body">
        {state.step === "setup" && (
          <AnalysisSetup
            text={text}
            stores={stores}
            context={context}
            disabled={!editable}
            canContinue={setupComplete(state)}
            onContext={(value) => dispatch({ type: "context", value })}
            onNext={() => navigate("files")}
          />
        )}
        {state.step === "files" && (
          <FilesStep
            text={text}
            lang={lang}
            state={state}
            plan={plan}
            limits={limits}
            disabled={!editable}
            notice={filesNotice}
            onToggleApp={(app) => dispatch({ type: "toggleApp", app })}
            onAdd={(app, section, files) => dispatch({ type: "addFiles", app, section, files, limits })}
            onReplace={(app, section, part, file) => dispatch({ type: "addFiles", app, section, files: [file], replace: part, limits })}
            onRemove={(id) => dispatch({ type: "removeUpload", id })}
            onDownload={(part) => openLudFile(part.currentFile, { token, storeId: context.storeCode, kind: "report", reference: part._id || part.id })}
            onBack={() => navigate("setup")}
            onNext={checkFiles}
          />
        )}
        {state.step === "validate" && (
          <ChecksStep
            text={text}
            lang={lang}
            state={state}
            review={review}
            busy={busy}
            onBack={() => navigate("files")}
            onRetry={checkFiles}
            onContinue={continueToPreview}
            onAcknowledge={acknowledge}
          />
        )}
        {state.step === "preview" && (
          <PreviewStep
            text={text}
            lang={lang}
            state={state}
            review={review}
            busy={busy}
            onBack={() => navigate("validate")}
            onRetry={continueToPreview}
            onSave={commit}
          />
        )}
        {state.step === "result" && (
          <ResultStep
            text={text}
            lang={lang}
            state={state}
            review={review}
            savingApps={state.apps}
            onRetry={commit}
            onBackToPreview={() => navigate("preview")}
            onBackToFiles={() => navigate("files")}
            onStartNew={startNew}
          />
        )}
      </div>
    </div>
  );
}
